import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "listView", "kanbanView",
    "listToggle", "kanbanToggle",
    "sortToggle", "sortAscIcon", "sortDescIcon",
    "todoColumn", "startedColumn", "endedColumn",
    "todoCount", "startedCount", "endedCount"
  ]

  connect() {
    this.isReorganizing = false
    this.isReversed = false
    this.boundHandleStreamRender = this.handleStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this.boundHandleStreamRender)

    const savedLayout = localStorage.getItem("win_card_layout_preference")
    if (savedLayout === "kanban") {
      this.distributeCardsToColumns()
      this.activateKanban()
    } else {
      this.activateList()
      const savedSort = localStorage.getItem("win_card_sort_preference")
      if (savedSort === "newest") {
        this.isReversed = true
        this._applyReverse()
        this._toggleSortHandles(false)
      }
    }
    this._updateSortButton(this.isReversed)

    if (this.hasSortToggleTarget) {
      this._boundSortClick = (e) => { e.preventDefault(); e.stopPropagation(); this.toggleSort() }
      this.sortToggleTarget.addEventListener('click', this._boundSortClick)
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.boundHandleStreamRender)
    if (this._boundSortClick && this.hasSortToggleTarget) {
      this.sortToggleTarget.removeEventListener('click', this._boundSortClick)
    }
  }

  switchToList(event) {
    if (event) event.preventDefault()
    this.moveCardsToList()
    this.activateList()
    localStorage.setItem("win_card_layout_preference", "list")
  }

  switchToKanban(event) {
    if (event) event.preventDefault()
    this.distributeCardsToColumns()
    this.activateKanban()
    localStorage.setItem("win_card_layout_preference", "kanban")
  }

  // --- Layout activation ---

  activateList() {
    if (!this.hasListViewTarget || !this.hasKanbanViewTarget) return
    this.listViewTarget.classList.remove("hidden")
    this.kanbanViewTarget.classList.add("hidden")
    this.updateToggleButtons("list")
  }

  activateKanban() {
    if (!this.hasListViewTarget || !this.hasKanbanViewTarget) return
    this.listViewTarget.classList.add("hidden")
    this.kanbanViewTarget.classList.remove("hidden")
    this.updateToggleButtons("kanban")
  }

  updateToggleButtons(mode) {
    if (!this.hasListToggleTarget || !this.hasKanbanToggleTarget) return
    const activeClasses = ["bg-gray-100", "text-gray-900"]
    const inactiveClasses = ["text-gray-400", "hover:text-gray-600"]

    if (mode === "list") {
      this.listToggleTarget.classList.add(...activeClasses)
      this.listToggleTarget.classList.remove(...inactiveClasses)
      this.kanbanToggleTarget.classList.remove(...activeClasses)
      this.kanbanToggleTarget.classList.add(...inactiveClasses)
    } else {
      this.kanbanToggleTarget.classList.add(...activeClasses)
      this.kanbanToggleTarget.classList.remove(...inactiveClasses)
      this.listToggleTarget.classList.remove(...activeClasses)
      this.listToggleTarget.classList.add(...inactiveClasses)
    }
  }

  isKanbanMode() {
    if (!this.hasKanbanViewTarget) return false
    return !this.kanbanViewTarget.classList.contains("hidden")
  }

  // --- Card distribution ---

  distributeCardsToColumns() {
    this.isReorganizing = true
    const listContainer = this.element.querySelector("#cards_list_content")
    if (!listContainer) { this.isReorganizing = false; return }

    const frames = Array.from(listContainer.querySelectorAll(":scope > turbo-frame"))
    frames.forEach(frame => {
      const card = frame.querySelector("[data-state]")
      if (!card) return
      const column = this.getColumnForState(card.dataset.state)
      if (column) column.appendChild(frame)
    })

    this.updateColumnCounts()
    this.updateColumnEmptyStates()
    this.isReorganizing = false
  }

  moveCardsToList() {
    this.isReorganizing = true
    const listContainer = this.element.querySelector("#cards_list_content")
    if (!listContainer) { this.isReorganizing = false; return }
    if (!this.hasTodoColumnTarget || !this.hasStartedColumnTarget || !this.hasEndedColumnTarget) { this.isReorganizing = false; return }

    const emptyState = listContainer.querySelector("[data-empty-state]")
    const columns = [this.todoColumnTarget, this.startedColumnTarget, this.endedColumnTarget]

    columns.forEach(column => {
      Array.from(column.querySelectorAll(":scope > turbo-frame")).forEach(frame => {
        if (emptyState) {
          listContainer.insertBefore(frame, emptyState)
        } else {
          listContainer.appendChild(frame)
        }
      })
    })

    // Reset list view filter to "All" so all cards are visible
    this.resetListFilter()

    if (this.isReversed) {
      this._applyReverse()
    }
    this.isReorganizing = false
  }

  resetListFilter() {
    // Remove hidden from all cards (in case a tab filter was active before kanban)
    const listContainer = this.element.querySelector("#cards_list_content")
    if (!listContainer) return

    listContainer.querySelectorAll('[data-card-status-filter-target="card"]').forEach(card => {
      card.classList.remove("hidden")
    })

    // Reset tab active states to "All"
    const filterEl = this.listViewTarget.querySelector('[data-controller="card-status-filter"]')
    if (!filterEl) return

    const allTab = filterEl.querySelector('[data-card-status-filter-target="allTab"]')
    const tabs = [
      filterEl.querySelector('[data-card-status-filter-target="allTab"]'),
      filterEl.querySelector('[data-card-status-filter-target="todoTab"]'),
      filterEl.querySelector('[data-card-status-filter-target="startedTab"]'),
      filterEl.querySelector('[data-card-status-filter-target="endedTab"]')
    ]

    tabs.forEach(tab => {
      if (!tab) return
      tab.classList.remove("border-blue-500", "text-blue-600")
      tab.classList.add("border-transparent", "text-gray-500")
    })

    if (allTab) {
      allTab.classList.remove("border-transparent", "text-gray-500")
      allTab.classList.add("border-blue-500", "text-blue-600")
    }

    // Hide the empty-state div
    const emptyState = listContainer.querySelector("[data-empty-state]")
    if (emptyState) emptyState.classList.add("hidden")
  }

  // --- Turbo Stream re-sorting ---

  handleStreamRender() {
    if (this.isKanbanMode()) {
      // Run after the stream has been applied to the DOM
      requestAnimationFrame(() => this.resortKanban())
    }
  }

  resortKanban() {
    if (this.isReorganizing) return
    this.isReorganizing = true

    const columns = [this.todoColumnTarget, this.startedColumnTarget, this.endedColumnTarget]
    columns.forEach(column => {
      const expectedState = column.dataset.kanbanState
      Array.from(column.querySelectorAll(":scope > turbo-frame")).forEach(frame => {
        const card = frame.querySelector("[data-state]")
        if (card && card.dataset.state !== expectedState) {
          const correctColumn = this.getColumnForState(card.dataset.state)
          if (correctColumn) correctColumn.appendChild(frame)
        }
      })
    })

    // Also check the list container for any cards that Turbo Stream appended there
    const listContainer = this.element.querySelector("#cards_list_content")
    if (listContainer) {
      Array.from(listContainer.querySelectorAll(":scope > turbo-frame")).forEach(frame => {
        const card = frame.querySelector("[data-state]")
        if (card) {
          const column = this.getColumnForState(card.dataset.state)
          if (column) column.appendChild(frame)
        }
      })
    }

    this.updateColumnCounts()
    this.updateColumnEmptyStates()
    this.isReorganizing = false
  }

  // --- Helpers ---

  getColumnForState(state) {
    switch (state) {
      case "todo": return this.hasTodoColumnTarget ? this.todoColumnTarget : null
      case "started": return this.hasStartedColumnTarget ? this.startedColumnTarget : null
      case "ended": return this.hasEndedColumnTarget ? this.endedColumnTarget : null
      default: return this.hasTodoColumnTarget ? this.todoColumnTarget : null
    }
  }

  updateColumnCounts() {
    const count = (column) => column.querySelectorAll(":scope > turbo-frame").length

    if (this.hasTodoCountTarget) {
      this.todoCountTarget.textContent = count(this.todoColumnTarget)
    }
    if (this.hasStartedCountTarget) {
      this.startedCountTarget.textContent = count(this.startedColumnTarget)
    }
    if (this.hasEndedCountTarget) {
      this.endedCountTarget.textContent = count(this.endedColumnTarget)
    }
  }

  updateColumnEmptyStates() {
    if (!this.hasTodoColumnTarget || !this.hasStartedColumnTarget || !this.hasEndedColumnTarget) return
    const columns = [
      { column: this.todoColumnTarget, state: "todo" },
      { column: this.startedColumnTarget, state: "started" },
      { column: this.endedColumnTarget, state: "ended" }
    ]

    columns.forEach(({ column }) => {
      const emptyState = column.querySelector("[data-kanban-empty]")
      if (!emptyState) return

      const hasCards = column.querySelectorAll(":scope > turbo-frame").length > 0
      emptyState.classList.toggle("hidden", hasCards)
    })
  }

  // --- Sort toggle ---

  toggleSort() {
    this.isReversed = !this.isReversed
    this._applyReverse()
    this._updateSortButton(this.isReversed)
    this._toggleSortHandles(!this.isReversed)
    localStorage.setItem("win_card_sort_preference", this.isReversed ? "newest" : "oldest")
  }

  _applyReverse() {
    const listContainer = this.element.querySelector("#cards_list_content")
    if (!listContainer) return
    const emptyState = listContainer.querySelector("[data-empty-state]")
    const frames = Array.from(listContainer.querySelectorAll(":scope > turbo-frame"))
    const idFrom = frame => parseInt(frame.id.replace("card_", ""), 10) || 0
    frames.sort((a, b) => this.isReversed ? idFrom(b) - idFrom(a) : idFrom(a) - idFrom(b))
    frames.forEach(frame => {
      if (emptyState) {
        listContainer.insertBefore(frame, emptyState)
      } else {
        listContainer.appendChild(frame)
      }
    })
  }

  _updateSortButton(reversed) {
    if (!this.hasSortToggleTarget) return
    const btn = this.sortToggleTarget
    if (reversed) {
      btn.classList.add("bg-gray-100", "text-gray-900")
      btn.classList.remove("text-gray-400", "hover:text-gray-600")
      btn.title = "Newest first — click for oldest first"
    } else {
      btn.classList.remove("bg-gray-100", "text-gray-900")
      btn.classList.add("text-gray-400", "hover:text-gray-600")
      btn.title = "Oldest first — click for newest first"
    }
    if (this.hasSortAscIconTarget) this.sortAscIconTarget.classList.toggle("hidden", reversed)
    if (this.hasSortDescIconTarget) this.sortDescIconTarget.classList.toggle("hidden", !reversed)
  }

  _toggleSortHandles(show) {
    this.element.querySelectorAll("[data-sort-handle]").forEach(handle => {
      handle.classList.toggle("hidden", !show)
    })
  }
}
